import React, { useEffect, useState } from "react";
import { Tag, Collapse, Space, Typography } from "antd";
import { WarningOutlined, FolderOutlined, CaretRightOutlined } from "@ant-design/icons";
import PageLoader from "./sub/PageLoader";
import MyPlanTreeCards from "./MyPlanTreeCard";
import { GetAssignedTasksApi } from "../api/tasks/GetAssignedTasksApi";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { getPluralString } from "../helpers/wordHelpers";

const { Panel } = Collapse;
const { Text } = Typography;

export default function MyPlansTabContent({
  planLevel,
  data,
  page,
  loading,
  tableLoading,
  loadMore,
  setPage,
  planCount,
  perfCycleId,
  positionId,
  dataLength
}) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [groupedSplitPlans, setGroupedSplitPlans] = useState([]);

  // Function to group split plans by their immediate parent plan (the plan they split from)
  function groupSplitPlansByParent(splitPlans) {
    console.log('🔍 [groupSplitPlansByParent] Input plans:', splitPlans);
    
    if (!splitPlans || splitPlans.length === 0) return [];

    const grouped = [];
    const usedPlanIds = new Set();

    // Create a map to store parent plan info for quick lookup
    const parentPlanMap = new Map();

    // Helper function to get the immediate parent plan title for categorization
    function getImmediateParentInfo(plan) {
      console.log('🔍 [getImmediateParentInfo] Processing plan:', plan.title, 'splitFrom:', plan.splitFrom, 'immediateParentTitle:', plan.immediateParentTitle, 'parentPlanTitle:', plan.parentPlanTitle);
      
      // Check if this is a split plan - either by splitFrom field or isSplitPlan flag
      const hasSplitFrom = plan.splitFrom && plan.splitFrom !== null && plan.splitFrom !== '';
      const isSplitPlan = plan.isSplitPlan === true;
      
      if (!hasSplitFrom && !isSplitPlan) {
        return null; // This is not a split plan
      }

      let parentId = null;
      let parentTitle = null;
      let categorySource = 'unknown';

      if (hasSplitFrom) {
        parentId = plan.splitFrom;
        
        // 🔧 CRITICAL: ALWAYS use immediateParentTitle first for proper categorization
        if (plan.immediateParentTitle && plan.immediateParentTitle.trim() !== '') {
          parentTitle = plan.immediateParentTitle;
          categorySource = 'immediate-parent';
          console.log('✅ Using immediateParentTitle:', parentTitle, 'for plan:', plan.title);
        } 
        // PRIORITY 2: Try to find the parent in current dataset (rare but possible)
        else {
          const parentPlan = splitPlans.find(p => (p.planId || p.id) === plan.splitFrom);
          if (parentPlan) {
            parentTitle = parentPlan.title;
            categorySource = 'current-dataset';
            console.log('✅ Found parent in current dataset:', parentTitle, 'for plan:', plan.title);
          } 
          // PRIORITY 3: Use parentPlanTitle as fallback (ultimate parent)
          else if (plan.parentPlanTitle && plan.parentPlanTitle.trim() !== '') {
            parentTitle = plan.parentPlanTitle;
            categorySource = 'ultimate-parent';
            console.log('⚠️ Fallback to parentPlanTitle:', parentTitle, 'for plan:', plan.title);
          } 
          // PRIORITY 4: Generic fallback
          else {
            parentTitle = `Parent Plan (${plan.splitFrom})`;
            categorySource = 'fallback';
            console.log('⚠️ Using generic fallback for splitFrom:', plan.splitFrom, 'for plan:', plan.title);
          }
        }
      }

      console.log('🔍 [getImmediateParentInfo] Final categorization:', {
        planTitle: plan.title,
        splitFromId: plan.splitFrom,
        parentTitle: parentTitle,
        categorySource: categorySource
      });

      return {
        id: parentId,
        title: parentTitle,
        source: categorySource
      };
    }

    // Group plans by their immediate parent
    splitPlans.forEach(plan => {
      const parentInfo = getImmediateParentInfo(plan);
      
      if (parentInfo) {
        const parentId = parentInfo.id;
        
        if (!parentPlanMap.has(parentId)) {
          parentPlanMap.set(parentId, {
            id: parentId,
            name: parentInfo.title,
            source: parentInfo.source,
            plans: []
          });
        }
        parentPlanMap.get(parentId).plans.push(plan);
        usedPlanIds.add(plan.planId || plan.id);
        
        console.log('✅ Added plan to group:', parentInfo.title, 'Plan:', plan.title, 'Source:', parentInfo.source);
      }
    });

    console.log('🔍 [groupSplitPlansByParent] Parent plan map:', parentPlanMap);

    // Convert map to array for rendering
    parentPlanMap.forEach((parentGroup, parentId) => {
      grouped.push({
        type: 'parent',
        parent: parentGroup,
        plans: parentGroup.plans,
        source: parentGroup.source,
        key: `parent-${parentId}`
      });
      console.log('✅ Created group:', parentGroup.name, 'with', parentGroup.plans.length, 'plans, source:', parentGroup.source);
    });

    // Add ungrouped plans (plans without splitFrom and not split plans)
    const ungroupedPlans = splitPlans.filter(plan => {
      const hasSplitFrom = plan.splitFrom && plan.splitFrom !== null && plan.splitFrom !== '';
      const isSplitPlan = plan.isSplitPlan === true;
      const isUsed = usedPlanIds.has(plan.planId || plan.id);
      return !hasSplitFrom && !isSplitPlan && !isUsed; // Plans that are not split plans AND are not already used
    });
    
    console.log('🔍 [groupSplitPlansByParent] Ungrouped plans:', ungroupedPlans);
    
    if (ungroupedPlans.length > 0) {
      grouped.push({
        type: 'ungrouped',
        parent: {
          id: 'ungrouped',
          name: 'Ungrouped Plans',
        },
        source: 'ungrouped',
        plans: ungroupedPlans,
        key: 'ungrouped'
      });
    }

    console.log('🔍 [groupSplitPlansByParent] FINAL CATEGORIZATION RESULTS:');
    grouped.forEach(group => {
      console.log(`📁 ${group.parent.name} (${group.plans.length} plans, source: ${group.source}):`);
      group.plans.forEach(plan => {
        console.log(`  - ${plan.title} (splitFrom: ${plan.splitFrom})`);
      });
    });

    return grouped;
  }

  // Check if current data contains plans with splitFrom field (split plans)
  function hasPlansWithSplitFrom(data) {
    if (!data || data.length === 0) return false;
    
    const result = data.some(plan => {
      // Check if this is a split plan - either by splitFrom field or isSplitPlan flag
      const hasSplitFrom = plan.splitFrom && plan.splitFrom !== null && plan.splitFrom !== '';
      const isSplitPlan = plan.isSplitPlan === true;
      return hasSplitFrom || isSplitPlan;
    });
    
    console.log('🔍 [hasPlansWithSplitFrom] Plan level:', data[0]?.planLevelName, 'Data length:', data?.length, 'Has split plans:', result);
    if (result && data && data.length > 0) {
      const splitPlans = data.filter(p => (p.splitFrom && p.splitFrom !== null && p.splitFrom !== '') || p.isSplitPlan === true);
      console.log('🔍 [hasPlansWithSplitFrom] Split plans found:', splitPlans.map(p => ({
        title: p.title, 
        splitFrom: p.splitFrom, 
        parentPlanTitle: p.parentPlanTitle, 
        planLevel: p.planLevelName
      })));
    }
    return result;
  }

  useEffect(() => {
    // Check if this is a Task tab (checking both singular and plural, case-insensitive)
    const isTaskTab = planLevel.name && (planLevel.name.toLowerCase() === "task" || planLevel.name.toLowerCase() === "tasks");
    if (isTaskTab) {
      fetchAssignedTasks();
    }
    // Note: Removed hardcoded Strategy tab check - let it be handled generically
  }, [planLevel, perfCycleId, positionId]);

  // Group split plans when data changes
  useEffect(() => {
    console.log('🔍 [MyPlansTabContent] Data changed:', data);
    console.log('🔍 [MyPlansTabContent] Plan level:', planLevel.name);
    
    if (data && data.length > 0) {
      console.log('🔍 [MyPlansTabContent] Sample plan data:', data[0]);
      console.log('🔍 [MyPlansTabContent] Has plans with splitFrom?', hasPlansWithSplitFrom(data));
    }
    
    if (data && hasPlansWithSplitFrom(data)) {
      console.log('🔍 [MyPlansTabContent] Grouping split plans...');
      const grouped = groupSplitPlansByParent(data);
      console.log('🔍 [MyPlansTabContent] Grouped result:', grouped);
      setGroupedSplitPlans(grouped);
    } else {
      setGroupedSplitPlans([]);
    }
  }, [data]);

  async function fetchAssignedTasks() {
    if (!positionId || !perfCycleId) {
      return;
    }
    
    setTasksLoading(true);
    try {
      const response = await GetAssignedTasksApi(positionId, perfCycleId);
      if (response.success) {
        setTasks(response.data || []);
      } else {
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
    setTasksLoading(false);
  }

  // Render Task tab content (checking both Task and Tasks, case-insensitive)
  const isTaskTab = planLevel.name && (planLevel.name.toLowerCase() === "task" || planLevel.name.toLowerCase() === "tasks");
  if (isTaskTab) {
    if (tasksLoading) {
      return <PageLoader />;
    }

    if (tasks.length === 0) {
      return (
        <div className="flex justify-center items-center" style={{ minHeight: 400 }}>
          <div className="text-center">
            <div className="text-3xl text-gray-400">
              <WarningOutlined />
            </div>
            <div className="text-xl pri-font text-gray-400">
              No Tasks available
            </div>
            <div className="text-gray-400">
              No tasks are assigned to you in this performance cycle
            </div>
          </div>
        </div>
      );
    }

    // Render tasks in a similar style to TopPlanTasks
    return (
      <div className="p-4">
        {tasks.map((task) => (
          <div key={task.id} className="flex items-start gap-4 border-b p-4 max-w-4xl">
            <div
              style={{
                border: "1px solid #1da57a",
                backgroundColor: task?.completionStatus ? "#1da57a" : "transparent",
              }}
              className="h-4 w-4 border rounded-sm mt-1"
            />

            <div className="flex items-start justify-between w-full">
              <div>
                <div 
                  className={`font-semibold ${task?.completionStatus ? "line-through" : ""} cursor-pointer hover:text-blue-500`}
                  onClick={() => navigate(`/top-plans/${task.planId}`)}
                >
                  {task.name}
                </div>
                <div className="text-gray-600 text-sm mt-1">
                  Plan: {task.plan?.title || '-'}
                </div>
                <div className="flex items-center gap-5 text-xs mt-2 text-gray-500">
                  {task?.taskStartDate && (
                    <div>
                      Start: {moment(task.taskStartDate).format("MMM DD, YYYY")}
                    </div>
                  )}
                  <div>
                    Target: {moment(task.taskEndDate).format("MMM DD, YYYY")}
                  </div>
                  {task?.completionStatus && task?.completionDate && (
                    <div>
                      Completed: {moment(task.completionDate).format("MMM DD, YYYY")}
                    </div>
                  )}
                  {task?.positions && (
                    <div>
                      Assigned to: {`${task.positions?.users?.firstName || ""} ${task.positions?.users?.lastName || ""}`.trim()} 
                      {task.positions?.designations?.name ? `(${task.positions.designations.name})` : ""}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Tag color={task?.completionStatus ? 'green' : 'orange'}>
                  {task?.completionStatus ? 'Completed' : 'Pending'}
                </Tag>
              </div>
            </div>
          </div>
        ))}
        {tasks.length > 0 && (
          <div className="mt-4 text-gray-500 text-sm">
            Total: {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
          </div>
        )}
      </div>
    );
  }

  // Default rendering for ALL tabs (including Strategy, Goal, Objective, Measures, etc.)
  if (loading) {
    return <PageLoader />;
  }
  
  if (dataLength > 0) {
    // Check if this data contains plans with splitFrom and should be grouped
    const hasSplitPlans = hasPlansWithSplitFrom(data);
    console.log('🔍 [MyPlansTabContent] Has split plans:', hasSplitPlans);
    console.log('🔍 [MyPlansTabContent] Grouped split plans length:', groupedSplitPlans.length);
    console.log('🔍 [MyPlansTabContent] Grouped split plans:', groupedSplitPlans);
    
    if (hasSplitPlans && groupedSplitPlans.length > 0) {
      console.log('🔍 [MyPlansTabContent] Rendering grouped view');
      return (
        <div className="p-6">
          <Collapse
            defaultActiveKey={groupedSplitPlans.map(group => group.key)}
            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            ghost
          >
            {groupedSplitPlans.map(group => (
              <Panel
                key={group.key}
                header={
                  <Space>
                    <FolderOutlined style={{ 
                      color: group.type === 'ungrouped' ? '#8c8c8c' : 
                             group.source === 'immediate-parent' ? '#52c41a' : '#1890ff' 
                    }} />
                    <Text strong>{group.parent.name}</Text>
                    <Tag color={
                      group.type === 'ungrouped' ? 'default' : 
                      group.source === 'immediate-parent' ? 'green' : 'blue'
                    }>
                      {group.plans.length} {group.plans.length === 1 ? 'plan' : 'plans'}
                    </Tag>
                    {group.source === 'immediate-parent' && (
                      <Tag color="success" size="small">
                        From splitFrom
                      </Tag>
                    )}
                    {group.source === 'ultimate-parent' && (
                      <Tag color="warning" size="small">
                        Fallback
                      </Tag>
                    )}
                  </Space>
                }
              >
                <MyPlanTreeCards
                  data={group.plans}
                  page={page}
                  loading={tableLoading}
                  loadMore={() => {}} // Disable load more for grouped view
                  setPage={setPage}
                  planCount={group.plans.length}
                />
              </Panel>
            ))}
          </Collapse>
        </div>
      );
    } else {
      console.log('🔍 [MyPlansTabContent] Rendering regular ungrouped view');
      // Regular ungrouped view for non-split plans
      return (
        <MyPlanTreeCards
          data={data}
          page={page}
          loading={tableLoading}
          loadMore={loadMore}
          setPage={setPage}
          planCount={planCount}
        />
      );
    }
  } else {
    // Only show "No plans" message when loading is complete
    return (
      <div className="flex justify-center items-center w-full h-full">
        <div className="text-center">
          <div className="text-3xl text-gray-400">
            <WarningOutlined />
          </div>
          <div className="text-xl pri-font text-gray-400">
            No {getPluralString(planLevel.name)} available
          </div>
        </div>
      </div>
    );
  }
}
